import React, { useContext, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiComment } from '@wix/da-papi-types';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SquareHeartFilledSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartFilled';
import SquareHeartOutlinedSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartOutlined';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { useVerification } from '../../../VerificationPopup/withVerification';
import { CommentContext } from '../../CommentContext';
import { BiData, ClickCommentLikeBiEvent } from '@wix/da-bi/pkg/events';

import s from './LikeButton.scss';

export interface Props {
  disabled?: boolean;
  className?: string;
  onLike: (comment: PapiComment) => void;
  onUnlike: (comment: PapiComment) => void;
}

const LikeButton: React.FC<Props> = ({ className, disabled, onLike, onUnlike }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { comment, isReduxReady } = useContext(CommentContext);
  const onMouseEnter = () => setIsHovered(true);
  const onMouseLeave = () => setIsHovered(false);

  const { restrictToVerifiedAndLoggedIn } = useVerification();
  const onToggleLike = useCallback(() => {
    // don't allow liking your own comment
    if (comment.isAuthor) {
      return;
    }

    if (comment.isLiked) {
      onUnlike(comment);
    } else {
      onLike(comment);
    }
  }, [comment, onLike, onUnlike]);
  const onClickLike = restrictToVerifiedAndLoggedIn(
    ReferralType.COMMENT_ACTION,
    'likecomment',
    onToggleLike
  );

  const { isAuthor, isLiked, likes } = comment;
  const canLike = !isAuthor;
  const hasLikes = likes > 0;

  const HeartIconSmall = isLiked ? SquareHeartFilledSmall : SquareHeartOutlinedSmall;
  const heartClasses = classnames(s['like'], isAuthor && s['not-clickable']);
  return (
    // Wrapper div because SimpleTooltip can render two elements at its root which will throw
    // off grid and flex layouts
    <div className={className} onPointerEnter={onMouseEnter} onPointerLeave={onMouseLeave}>
      <PlainButtonOrLink
        className={s['likebutton']}
        onClick={onClickLike}
        disabled={disabled ?? !isReduxReady}
        biData={
          canLike
            ? BiData<ClickCommentLikeBiEvent>({
                evid: 186,
                commenterid: comment.user?.useridUuid,
                commentid: comment.commentId,
                typeid: comment.typeId,
                itemid: comment.itemId,
                is_liking: isLiked ? 0 : 1,
              })
            : undefined
        }
        aria-label={t('comment.a11y.action.like')}
      >
        <SimpleTooltip
          listenOnClicks={false}
          forceVisible={isHovered}
          offsetModifier={[0, 2]}
          usePortal
          text={
            isLiked
              ? t('comment.action.like.tooltip.liked')
              : t('comment.action.like.tooltip.notLiked')
          }
        >
          <HeartIconSmall className={heartClasses} size={IconSize.SMALLER} disableDefaultColors />
        </SimpleTooltip>
        {hasLikes && (
          <span className={s['likes']}>
            {shortenNumber(likes)}
            <VisuallyHidden>{t('comment.action.likes', { count: likes })}</VisuallyHidden>
          </span>
        )}
      </PlainButtonOrLink>
    </div>
  );
};
LikeButton.displayName = 'LikeButton';

export default LikeButton;
